import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { authApi } from '@/api/auth';
import { Field } from '@/components/ui/field';
import { ApiError } from '@/lib/api-client';
import { resetPasswordSchema, type ResetPasswordInput } from '@/lib/form-schemas';

export function ResetPasswordPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirm_password: '' },
    mode: 'onSubmit',
  });

  const reset = useMutation({
    mutationFn: (v: ResetPasswordInput) =>
      authApi.resetPassword({ token, password: v.password }),
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await reset.mutateAsync(values);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Reset failed');
    }
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-mb-bg p-6">
      <div className="mb-card p-10 max-w-md w-full">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-lg bg-mb-accent-soft text-mb-accent-press flex items-center justify-center mb-4">
            <KeyRound className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <h1 className="font-display text-xl font-bold text-mb-text">
            Choose a new password
          </h1>
        </div>

        {done ? (
          <div className="mt-6 rounded-md border border-mb-success/30 bg-mb-success-soft p-4 text-sm text-mb-text">
            Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
            <Field
              label="New password"
              hint="At least 8 characters"
              required
              error={form.formState.errors.password?.message}
            >
              <input
                type="password"
                autoComplete="new-password"
                className="mb-input"
                {...form.register('password')}
              />
            </Field>
            <Field
              label="Confirm new password"
              required
              error={form.formState.errors.confirm_password?.message}
            >
              <input
                type="password"
                autoComplete="new-password"
                className="mb-input"
                {...form.register('confirm_password')}
              />
            </Field>
            {submitError ? (
              <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
                {submitError}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={reset.isPending || form.formState.isSubmitting}
              className="mb-btn-primary w-full"
            >
              {reset.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Set new password
            </button>
          </form>
        )}

        <p className="text-xs text-mb-text-muted text-center mt-6">
          <Link to="/login" className="text-mb-accent-press hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
